import type {CreateConnectionDataADto} from '@cohort/admin-schemas/connection';
import type {ConnectionEditComponentProps} from '@cohort/merchants/apps';
import Button from '@cohort/merchants/components/buttons/Button';
import Input from '@cohort/merchants/components/form/v2/Input';
import Header from '@cohort/merchants/components/Header';
import {connectionsKeys} from '@cohort/merchants/hooks/api/Connections';
import {useCohortMutation} from '@cohort/merchants/hooks/api/Query';
import {useUserSessionStore} from '@cohort/merchants/hooks/stores/userSession';
import {createConnection} from '@cohort/merchants/lib/api/Connections';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import {REDACTED_FIELD} from '@cohort/shared/constants';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQueryClient} from '@tanstack/react-query';
import {useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {z} from 'zod';

const NOTION_LINK =
  'https://cohort-xyz.notion.site/Setup-Discord-Integration-9355a3ca5d314eb3ad336b28e96eb85f';

const DiscordConnectionSchema = z.object({
  guildId: z.string().regex(/^\d+$/u, 'errorFormat'),
});
type DiscordConnectionType = z.infer<typeof DiscordConnectionSchema>;

const DiscordEditConnectionComponent: React.FC<ConnectionEditComponentProps> = ({
  existingConnectionId,
  onClose,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const merchantId = useUserSessionStore(store => store.merchantId!);
  const queryClient = useQueryClient();
  const {t} = useTranslation('app-discord', {
    keyPrefix: 'editConnectionComponent',
  });
  const {register, handleSubmit, control} = useForm<DiscordConnectionType>({
    resolver: zodResolver(DiscordConnectionSchema),
    defaultValues: {
      guildId: existingConnectionId ? REDACTED_FIELD : undefined,
    },
  });

  const {isLoading, mutate: createConnectionMutation} = useCohortMutation({
    mutationFn: async (data: CreateConnectionDataADto) => createConnection(merchantId, data),
    notifySuccessMessage: t('createSuccessNotification'),
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionsKeys.list(merchantId));
      onClose();
    },
    onError: err => notifyError(err, t('errorInvalidCredentials')),
  });

  return (
    <form
      onSubmit={e => {
        e.stopPropagation();
        handleSubmit(async data => {
          createConnectionMutation({
            connectorId: 'discord-merchant',
            name: null,
            credentials: {
              guildId: data.guildId,
            },
          });
        })(e);
      }}
      className="space-y-6 p-6"
      data-testid="create-connection-form"
      id="create-connection-form"
    >
      <Header title={t('title')} subtitle={t('subtitle')} />
      <Input
        type="text"
        name="guildId"
        label={t('labelServerId')}
        description={
          <Trans
            i18nKey="editConnectionComponent.description"
            ns="app-discord"
            components={{
              notionLink: (
                <a
                  className="cursor-pointer font-semibold text-primary"
                  href={NOTION_LINK}
                  target="_blank"
                  rel="noreferrer"
                />
              ),
            }}
          />
        }
        register={register}
        control={control}
      />
      <Button
        data-testid="create-digital-asset-collection-submit-btn"
        type="submit"
        form="create-connection-form"
        loading={isLoading}
      >
        {t('buttonCreate')}
      </Button>
    </form>
  );
};

export default DiscordEditConnectionComponent;
